<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  mode: {
    type: String,
    default: 'text',
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['submit']);

const { t } = useI18n();
const dialogRef = ref(null);
const content = ref('');
const file = ref(null);
const fileInput = ref(null);

const isPdf = computed(() => props.mode === 'pdf');
const title = computed(() =>
  isPdf.value
    ? t('ONIX.KNOWLEDGE.UPLOAD_TITLE')
    : t('ONIX.KNOWLEDGE.WRITE_TITLE')
);
const canConfirm = computed(() =>
  isPdf.value ? !!file.value : !!content.value.trim()
);

const open = () => {
  content.value = '';
  file.value = null;
  if (fileInput.value) fileInput.value.value = '';
  dialogRef.value?.open();
};

const onFile = event => {
  file.value = event.target.files?.[0] || null;
};

const confirm = () => {
  if (!canConfirm.value) return;
  emit(
    'submit',
    isPdf.value ? { file: file.value } : { content: content.value.trim() }
  );
};

defineExpose({ open, close: () => dialogRef.value?.close() });
</script>

<template>
  <Dialog
    ref="dialogRef"
    :title="title"
    :description="isPdf ? t('ONIX.KNOWLEDGE.UPLOAD_HINT') : ''"
    :confirm-button-label="t('ONIX.KNOWLEDGE.SAVE_TEXT')"
    :disable-confirm-button="!canConfirm"
    :is-loading="isLoading"
    @confirm="confirm"
  >
    <TextArea
      v-if="!isPdf"
      v-model="content"
      :placeholder="t('ONIX.KNOWLEDGE.WRITE_PLACEHOLDER')"
      :max-length="20000"
      auto-height
      min-height="8rem"
    />
    <div v-else class="flex flex-col gap-2">
      <input
        ref="fileInput"
        type="file"
        accept="application/pdf,.pdf"
        class="sr-only"
        @change="onFile"
      />
      <Button
        type="button"
        :label="file ? file.name : t('ONIX.KNOWLEDGE.CHOOSE_FILE')"
        color="slate"
        variant="faded"
        size="sm"
        class="self-start"
        @click="fileInput?.click()"
      />
    </div>
  </Dialog>
</template>
